import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Subject, Subscription, of, switchMap, takeUntil } from 'rxjs';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { formatStat, snakeCaseToTitleCase } from '../../utils/format.utils';

type StyleMap = Record<string, string>;
type IconType = 'mat' | 'emoji' | 'text';

interface StatRangeConfig {
  min?: number;
  max?: number;
  color?: string;
  status?: string;
  icon?: string;
  iconType?: IconType;
  backgroundColor?: string;
  textColor?: string;
  includeMin?: boolean;
  includeMax?: boolean;
}

interface StatIconConfig {
  icon?: string;
  iconType?: IconType;
  color?: string;
  backgroundColor?: string;
  status?: string;
}

interface StatRow {
  id: string;
  label: string;
  value: string;
  rawValue: unknown;
  rowStyle: StyleMap;
  labelStyle: StyleMap;
  valueStyle: StyleMap;
}

interface StatCard {
  id: string;
  title: string;
  value: string;
  rawValue: unknown;
  delta: string;
  rows: StatRow[];
  icon: string;
  iconType: IconType;
  statusClass: string;
  cardStyle: StyleMap;
  titleStyle: StyleMap;
  valueStyle: StyleMap;
  deltaStyle: StyleMap;
  iconStyle: StyleMap;
}

@Component({
  selector: 'app-number-stats-n-pat',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './number-stats-n-pat.component.html',
  styleUrl: './number-stats-n-pat.component.scss',
})
export class NumberStatsNPatComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;

  cards: StatCard[] = [];
  hasError = false;
  errorMessage = '';
  errorIcon = '';
  gridStyle: StyleMap = {};
  containerStyle: StyleMap = {};

  private filterSub?: Subscription;
  private unSubscribe$ = new Subject<void>();

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private chartFilterHandler: ChartFilterHandlerService,
  ) {}

  ngAfterViewInit(): void {
    this.applyLayoutStyles();

    this.filterSub = this.loadStats(this.getInitialFiltersForWidget())
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: unknown) => this.updateCards(res),
        error: (err: unknown) => {
          this.hasError = true;
          this.errorMessage = 'Error loading stats.';
          console.error('Error loading stats:', err);
        },
      });

    this.globalFilterService.filter$
      .pipe(
        takeUntil(this.unSubscribe$),
        switchMap((filters) => {
          if (!this.chartFilterHandler.shouldReload(filters, this.data?.widgetId)) {
            return of(null);
          }

          if (this.data?.callApiInDefault === false) {
            const missingMandatoryFilterNames = this.getMissingMandatoryFilterNames(filters);

            if (missingMandatoryFilterNames.length) {
              this.hasError = true;
              this.errorMessage = `Please select ${missingMandatoryFilterNames.join(' and ')} to see the data.`;
              return of(null);
            }
          }

          this.hasError = false;
          return this.loadStats(filters);
        }),
      )
      .subscribe({
        next: (res: unknown) => {
          if (res === null) return;
          this.updateCards(res);
        },
        error: (err: unknown) => {
          this.hasError = true;
          this.errorMessage = 'Error loading stats.';
          console.error('Error loading stats:', err);
        },
      });
  }

  loadStats(filters?: any): any {
    return this.configService.getConfig(
      this.data?.apiUrl,
      this.data,
      {
        ...filters,
        useQueryParams: this.data?.useQueryParams || false,
      },
      this.data?.method || 'post',
    );
  }

  trackByCard(_index: number, card: StatCard): string {
    return card.id;
  }

  trackByRow(_index: number, row: StatRow): string {
    return row.id;
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  private apiConfig: Record<string, any> = {};

  private get config(): Record<string, any> {
    return {
      ...(this.isRecord(this.data?.chartConfig) ? this.data.chartConfig : {}),
      ...(this.isRecord(this.data?.statsConfig) ? this.data.statsConfig : {}),
      ...this.apiConfig,
    };
  }

  private applyLayoutStyles(): void {
    const config = this.config;
    const columns = this.toPositiveNumber(config['columns'] ?? this.data?.columns);
    const cardMinWidth = this.toCssUnit(config['cardMinWidth'] ?? this.data?.cardMinWidth ?? 220);
    const gap = this.toCssUnit(config['gap'] ?? this.data?.gap ?? '1rem');

    this.gridStyle = {
      gap,
      '--pat-card-min-width': cardMinWidth,
      'grid-template-columns': columns
        ? `repeat(${columns}, minmax(0, 1fr))`
        : 'repeat(auto-fit, minmax(min(100%, var(--pat-card-min-width)), 1fr))',
      ...this.normalizeStyle(config['gridStyle'] ?? this.data?.gridStyle),
    };
    this.containerStyle = this.normalizeStyle(config['containerStyle'] ?? this.data?.containerStyle);
  }

  private getInitialFiltersForWidget(): any | undefined {
    const navigationFilters = this.globalFilterService.getNavigationFiltersForWidget(this.data?.widgetId);
    if (navigationFilters) {
      return navigationFilters;
    }

    const filters = this.globalFilterService.getCurrentFilters();
    const hasFilters = !!(
      Object.keys(filters?.queryParams || {}).length ||
      Object.keys(filters?.requestParams || {}).length ||
      filters?.filterListener
    );

    if (!hasFilters) {
      return undefined;
    }

    return this.chartFilterHandler.shouldReload(filters, this.data?.widgetId) ? filters : undefined;
  }

  private getMissingMandatoryFilterNames(filters: any): string[] {
    const mandatoryFilters = Array.isArray(this.data?.mandatoryFilters)
      ? this.data.mandatoryFilters
      : [];

    return mandatoryFilters.filter((mandatoryFilterKey: string) => {
      const filterValue =
        filters?.queryParams?.[mandatoryFilterKey] ||
        filters?.requestParams?.[mandatoryFilterKey];
      return !filterValue || filterValue.length === 0;
    }).map((mandatoryFilterKey: string) => snakeCaseToTitleCase(mandatoryFilterKey));
  }

  private updateCards(response: unknown): void {
    if (this.isRecord(response)) {
      this.errorIcon = typeof response['errorIcon'] === 'string' ? response['errorIcon'] : '';
      this.apiConfig = {
        ...(this.isRecord(response['config']) ? response['config'] : {}),
        ...(this.isRecord(response['chartConfig']) ? response['chartConfig'] : {}),
        ...(this.isRecord(response['statsConfig']) ? response['statsConfig'] : {}),
        ...(Array.isArray(response['statusRanges']) ? { statusRanges: response['statusRanges'] } : {}),
        ...(Array.isArray(response['ranges']) ? { ranges: response['ranges'] } : {}),
        ...(this.isRecord(response['styles']) ? { styles: response['styles'] } : {}),
        ...(this.isRecord(response['stylesByTitle']) ? { stylesByTitle: response['stylesByTitle'] } : {}),
        ...(this.isRecord(response['iconsByTitle']) ? { iconsByTitle: response['iconsByTitle'] } : {}),
        ...(response['columns'] !== undefined ? { columns: response['columns'] } : {}),
        ...(response['gap'] !== undefined ? { gap: response['gap'] } : {}),
        ...(response['cardMinWidth'] !== undefined ? { cardMinWidth: response['cardMinWidth'] } : {}),
      };
    } else {
      this.apiConfig = {};
    }

    this.applyLayoutStyles();

    const source = this.resolveResponseSource(response);
    const cards = this.buildCards(source);

    if (!cards.length) {
      this.cards = [];
      this.hasError = true;
      this.errorMessage = 'No data found for the selected filters.';
      return;
    }

    this.cards = cards;
    this.hasError = false;
    this.errorMessage = '';
  }

  private resolveResponseSource(response: unknown): unknown {
    const config = this.config;
    const configuredPath = config['dataPath'] ?? this.data?.dataPath;
    const pathValue = typeof configuredPath === 'string'
      ? this.getByPath(response, configuredPath)
      : undefined;

    if (pathValue !== undefined) {
      return pathValue;
    }

    if (this.isRecord(response)) {
      return response['responseList']?.[0]?.['data']?.[0]
        ?? response['data']?.[0]
        ?? response['data']
        ?? response;
    }

    return response;
  }

  private buildCards(source: unknown): StatCard[] {
    const records = this.resolveCardRecords(source);
    return records.map((record, index) => this.toCard(record.value, record.key, index));
  }

  private resolveCardRecords(source: unknown): Array<{ key: string; value: unknown }> {
    const config = this.config;
    const configuredListKeys = [
      config['cardsKey'],
      config['statsKey'],
      config['itemsKey'],
      config['listKey'],
      this.data?.cardsKey,
      this.data?.statsKey,
      this.data?.itemsKey,
    ].filter((key): key is string => typeof key === 'string' && !!key.trim());

    for (const key of configuredListKeys) {
      const value = this.getByPath(source, key);
      if (Array.isArray(value)) {
        return value.map((item, index) => ({ key: this.getRecordKey(item, index), value: item }));
      }
    }

    if (Array.isArray(source)) {
      return source.map((item, index) => ({ key: this.getRecordKey(item, index), value: item }));
    }

    if (this.isRecord(source)) {
      const commonArrayKeys = ['cards', 'stats', 'items', 'metrics', 'values', 'records', 'rows'];
      for (const key of commonArrayKeys) {
        const value = source[key];
        if (Array.isArray(value)) {
          return value.map((item, index) => ({ key: this.getRecordKey(item, index), value: item }));
        }
      }

      return Object.entries(source)
        .filter(([key, value]) => this.isDisplayEntry(key, value))
        .map(([key, value]) => ({ key, value }));
    }

    if (source !== null && source !== undefined && source !== '') {
      return [{ key: String(this.data?.title || this.data?.key || 'value'), value: source }];
    }

    return [];
  }

  private toCard(record: unknown, fallbackKey: string, index: number): StatCard {
    const rawRecord = this.isRecord(record) ? record : {};
    const title = this.resolveTitle(rawRecord, fallbackKey, index);
    const rows = this.resolveRows(record, title);
    const rawValue = rows.length ? null : this.resolveValue(record, fallbackKey);
    const value = rows.length ? '' : this.formatValue(rawValue, fallbackKey);
    const deltaRawValue = this.resolveFirst(rawRecord, ['delta', 'change', 'changePercent', 'changePct', 'gap']);
    const delta = deltaRawValue === undefined || deltaRawValue === null ? '' : String(deltaRawValue);
    const range = this.resolveRange(this.resolveRangeValue(rawRecord, rawValue));
    const iconConfig = this.resolveIconConfig(title, rawRecord, range);
    const status = this.sanitizeCssClass(iconConfig.status || range?.status || range?.color || '');

    return {
      id: `${this.sanitizeCssClass(title) || 'stat'}-${index}`,
      title,
      value,
      rawValue,
      delta,
      rows,
      icon: iconConfig.icon || '',
      iconType: iconConfig.iconType || 'mat',
      statusClass: status ? `status-${status}` : '',
      cardStyle: this.resolveMergedStyle('card', title, rawRecord),
      titleStyle: this.resolveMergedStyle('title', title, rawRecord),
      valueStyle: this.resolveValueStyle('value', title, rawRecord, range),
      deltaStyle: this.resolveValueStyle('delta', title, rawRecord, range),
      iconStyle: this.resolveIconStyle(iconConfig, range),
    };
  }

  private resolveRows(record: unknown, cardTitle: string): StatRow[] {
    const rowRecords = this.resolveRowRecords(record);

    return rowRecords.map((rowRecord, index) => {
      const rawRow = this.isRecord(rowRecord.value) ? rowRecord.value : {};
      const label = this.resolveRowLabel(rawRow, rowRecord.key, index);
      const rawValue = this.resolveValue(rowRecord.value, rowRecord.key);
      const range = this.resolveRange(this.resolveRangeValue(rawRow, rawValue));

      return {
        id: `${this.sanitizeCssClass(cardTitle)}-${this.sanitizeCssClass(label) || index}`,
        label,
        value: this.formatValue(rawValue, rowRecord.key),
        rawValue,
        rowStyle: this.resolveMergedStyle('row', label, rawRow, cardTitle),
        labelStyle: this.resolveMergedStyle('rowLabel', label, rawRow, cardTitle),
        valueStyle: this.resolveValueStyle('rowValue', label, rawRow, range, cardTitle),
      };
    });
  }

  private resolveRowRecords(record: unknown): Array<{ key: string; value: unknown }> {
    if (!this.isRecord(record)) {
      return [];
    }

    const config = this.config;
    const configuredRowsKey = config['rowsKey'] ?? config['detailsKey'] ?? this.data?.rowsKey;
    if (typeof configuredRowsKey === 'string') {
      const value = this.getByPath(record, configuredRowsKey);
      if (Array.isArray(value)) {
        return value.map((item, index) => ({ key: this.getRecordKey(item, index), value: item }));
      }
    }

    const commonRowsKeys = ['rows', 'items', 'details', 'breakdown', 'children'];
    for (const key of commonRowsKeys) {
      const value = record[key];
      if (Array.isArray(value)) {
        return value.map((item, index) => ({ key: this.getRecordKey(item, index), value: item }));
      }
    }

    const value = this.resolveValue(record, '');
    if (value !== undefined) {
      return [];
    }

    return Object.entries(record)
      .filter(([key, entryValue]) => this.isDisplayEntry(key, entryValue))
      .map(([key, entryValue]) => ({ key, value: entryValue }));
  }

  private resolveTitle(record: Record<string, unknown>, fallbackKey: string, index: number): string {
    const config = this.config;
    const titleFields = [
      config['titleField'],
      config['headingField'],
      this.data?.titleField,
      'title',
      'heading',
      'label',
      'name',
      'displayName',
      'metric',
      'key',
    ].filter((field): field is string => typeof field === 'string' && !!field.trim());

    const title = this.resolveFirst(record, titleFields);
    if (title !== undefined && title !== null && String(title).trim()) {
      return String(title);
    }

    if (fallbackKey && !/^\d+$/.test(fallbackKey)) {
      return snakeCaseToTitleCase(fallbackKey);
    }

    return `${this.data?.title || 'Stat'} ${index + 1}`;
  }

  private resolveRowLabel(record: Record<string, unknown>, fallbackKey: string, index: number): string {
    const config = this.config;
    const labelFields = [
      config['rowLabelField'],
      config['labelField'],
      'label',
      'title',
      'heading',
      'name',
      'displayName',
      'metric',
      'key',
    ].filter((field): field is string => typeof field === 'string' && !!field.trim());

    const label = this.resolveFirst(record, labelFields);
    if (label !== undefined && label !== null && String(label).trim()) {
      return String(label);
    }

    if (fallbackKey && !/^\d+$/.test(fallbackKey)) {
      return snakeCaseToTitleCase(fallbackKey);
    }

    return `Item ${index + 1}`;
  }

  private resolveValue(record: unknown, fallbackKey: string): unknown {
    if (!this.isRecord(record)) {
      return record;
    }

    const config = this.config;
    const valueFields = [
      config['valueField'],
      this.data?.valueField,
      'value',
      'stat',
      'count',
      'amount',
      'total',
      'number',
      'metricValue',
    ].filter((field): field is string => typeof field === 'string' && !!field.trim());

    const value = this.resolveFirst(record, valueFields);
    if (value !== undefined) {
      return value;
    }

    return record[fallbackKey];
  }

  private resolveRangeValue(record: Record<string, unknown>, fallbackValue: unknown): unknown {
    const config = this.config;
    const rangeValueField = config['rangeValueField'] ?? this.data?.rangeValueField;
    if (typeof rangeValueField === 'string') {
      const value = this.getByPath(record, rangeValueField);
      if (value !== undefined) {
        return value;
      }
    }

    const rangeValue = this.resolveFirst(record, ['rangeValue', 'score', 'percentage', 'percent']);
    return rangeValue !== undefined ? rangeValue : fallbackValue;
  }

  private resolveRange(value: unknown): StatRangeConfig | null {
    const config = this.config;
    const ranges = [
      ...(Array.isArray(config['statusRanges']) ? config['statusRanges'] : []),
      ...(Array.isArray(config['ranges']) ? config['ranges'] : []),
      ...(Array.isArray(this.data?.statusRanges) ? this.data.statusRanges : []),
      ...(Array.isArray(this.data?.ranges) ? this.data.ranges : []),
    ];

    const numericValue = this.toNumber(value);
    if (numericValue === null) {
      return null;
    }

    return ranges.find((range: StatRangeConfig) => {
      const min = typeof range.min === 'number' ? range.min : Number.NEGATIVE_INFINITY;
      const max = typeof range.max === 'number' ? range.max : Number.POSITIVE_INFINITY;
      const includeMin = range.includeMin !== false;
      const includeMax = range.includeMax !== false;
      const passesMin = includeMin ? numericValue >= min : numericValue > min;
      const passesMax = includeMax ? numericValue <= max : numericValue < max;
      return passesMin && passesMax;
    }) ?? null;
  }

  private resolveIconConfig(
    title: string,
    record: Record<string, unknown>,
    range: StatRangeConfig | null,
  ): StatIconConfig {
    const config = this.config;
    const iconMaps = [
      config['iconsByTitle'],
      config['icons'],
      this.data?.iconsByTitle,
      this.data?.icons,
    ].filter((map): map is Record<string, unknown> => this.isRecord(map));
    const mappedIcon = this.findMappedConfig(iconMaps, title);
    const directIcon = this.normalizeIconConfig(mappedIcon);

    return {
      icon: directIcon.icon || range?.icon || this.toOptionalString(record['icon']),
      iconType: directIcon.iconType || range?.iconType || this.toIconType(record['iconType']),
      color: directIcon.color || range?.textColor || range?.color || this.toOptionalString(record['iconColor']),
      backgroundColor: directIcon.backgroundColor || range?.backgroundColor || this.toOptionalString(record['iconBackgroundColor']),
      status: directIcon.status || range?.status || range?.color || this.toOptionalString(record['status']),
    };
  }

  private normalizeIconConfig(value: unknown): StatIconConfig {
    if (typeof value === 'string') {
      return { icon: value };
    }

    if (!this.isRecord(value)) {
      return {};
    }

    return {
      icon: this.toOptionalString(value['icon']),
      iconType: this.toIconType(value['iconType']),
      color: this.toOptionalString(value['color'] ?? value['textColor']),
      backgroundColor: this.toOptionalString(value['backgroundColor'] ?? value['bgColor']),
      status: this.toOptionalString(value['status']),
    };
  }

  private resolveIconStyle(iconConfig: StatIconConfig, range: StatRangeConfig | null): StyleMap {
    const palette = this.resolvePalette(iconConfig.status || range?.status || range?.color || '');
    const color = iconConfig.color || range?.textColor || palette.color;
    const backgroundColor = iconConfig.backgroundColor || range?.backgroundColor || palette.backgroundColor;

    return {
      '--pat-status-color': color,
      '--pat-status-bg': backgroundColor,
    };
  }

  private resolveValueStyle(
    styleKey: string,
    lookupKey: string,
    record: Record<string, unknown>,
    range: StatRangeConfig | null,
    cardTitle?: string,
  ): StyleMap {
    const style = this.resolveMergedStyle(styleKey, lookupKey, record, cardTitle);
    const palette = this.resolvePalette(range?.status || range?.color || '');
    const color = range?.textColor || palette.textColor || palette.color;

    return color && !style['color'] ? { ...style, color } : style;
  }

  private resolveMergedStyle(
    styleKey: string,
    lookupKey: string,
    record: Record<string, unknown>,
    cardTitle?: string,
  ): StyleMap {
    const config = this.config;
    const styleMaps = [
      config['styles'],
      this.data?.styles,
      config['style'],
      this.data?.style,
    ].filter((map): map is Record<string, unknown> => this.isRecord(map));
    const titleMaps = [
      config['stylesByTitle'],
      config['stylesByLabel'],
      this.data?.stylesByTitle,
      this.data?.stylesByLabel,
    ].filter((map): map is Record<string, unknown> => this.isRecord(map));
    const recordStyle = this.isRecord(record['styles']) ? record['styles'] : record['style'];
    const cardStyleConfig = cardTitle ? this.findMappedConfig(titleMaps, cardTitle) : undefined;
    const lookupStyleConfig = this.findMappedConfig(titleMaps, lookupKey);

    return {
      ...this.normalizeStyle(this.resolveFirstStyle(styleMaps, styleKey)),
      ...this.normalizeStyle(this.isRecord(cardStyleConfig) ? cardStyleConfig[styleKey] : undefined),
      ...this.normalizeStyle(this.isRecord(lookupStyleConfig) ? lookupStyleConfig[styleKey] : undefined),
      ...this.normalizeStyle(this.isRecord(recordStyle) ? recordStyle[styleKey] : recordStyle),
    };
  }

  private resolveFirstStyle(styleMaps: Array<Record<string, unknown>>, styleKey: string): unknown {
    for (const styleMap of styleMaps) {
      if (styleMap[styleKey] !== undefined) {
        return styleMap[styleKey];
      }
    }

    return undefined;
  }

  private formatValue(value: unknown, key: string): string {
    const config = this.config;
    const useFormatStat = this.data?.useFormatStat === true || config['useFormatStat'] === true;
    if (useFormatStat) {
      return formatStat(value, key || this.data?.key || '', this.data?.prefix || config['prefix'] || '');
    }

    return value === undefined || value === null ? '' : String(value);
  }

  private getRecordKey(record: unknown, index: number): string {
    if (this.isRecord(record)) {
      const key = this.resolveFirst(record, ['key', 'id', 'name', 'title', 'label', 'heading']);
      if (key !== undefined && key !== null && String(key).trim()) {
        return String(key);
      }
    }

    return String(index);
  }

  private isDisplayEntry(key: string, value: unknown): boolean {
    const hiddenKeys = new Set([
      'id',
      'key',
      'title',
      'heading',
      'label',
      'name',
      'displayName',
      'metric',
      'icon',
      'iconType',
      'iconColor',
      'iconBackgroundColor',
      'status',
      'delta',
      'change',
      'changePercent',
      'changePct',
      'gap',
      'styles',
      'style',
      'rangeValue',
      'score',
      'percentage',
      'percent',
    ]);

    if (hiddenKeys.has(key)) {
      return false;
    }

    return value !== undefined && value !== null && value !== '';
  }

  private getByPath(source: unknown, path: string): unknown {
    if (!path.trim()) {
      return source;
    }

    return path.split('.').reduce<unknown>((current, segment) => {
      if (this.isRecord(current)) {
        return current[segment];
      }

      if (Array.isArray(current)) {
        const index = Number(segment);
        return Number.isInteger(index) ? current[index] : undefined;
      }

      return undefined;
    }, source);
  }

  private resolveFirst(record: Record<string, unknown>, fields: string[]): unknown {
    for (const field of fields) {
      const value = this.getByPath(record, field);
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    return undefined;
  }

  private findMappedConfig(maps: Array<Record<string, unknown>>, key: string): unknown {
    const normalizedKey = this.normalizeLookupKey(key);
    for (const map of maps) {
      if (map[key] !== undefined) {
        return map[key];
      }

      const matchingEntry = Object.entries(map).find(([entryKey]) => this.normalizeLookupKey(entryKey) === normalizedKey);
      if (matchingEntry) {
        return matchingEntry[1];
      }
    }

    return undefined;
  }

  private normalizeLookupKey(key: string): string {
    return key.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private normalizeStyle(value: unknown): StyleMap {
    if (!this.isRecord(value)) {
      return {};
    }

    return Object.entries(value).reduce<StyleMap>((style, [key, entryValue]) => {
      if (entryValue !== undefined && entryValue !== null && entryValue !== '') {
        style[key] = typeof entryValue === 'number' ? String(entryValue) : String(entryValue);
      }
      return style;
    }, {});
  }

  private toCssUnit(value: unknown): string {
    if (typeof value === 'number') {
      return `${value}px`;
    }

    if (typeof value === 'string' && value.trim()) {
      return /^\d+(\.\d+)?$/.test(value.trim()) ? `${value.trim()}px` : value.trim();
    }

    return '0';
  }

  private toPositiveNumber(value: unknown): number | null {
    const numericValue = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? Math.round(numericValue) : null;
  }

  private toNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const numericText = value.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (!numericText) {
      return null;
    }

    const numericValue = Number(numericText[0]);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private toOptionalString(value: unknown): string | undefined {
    return value === undefined || value === null || value === '' ? undefined : String(value);
  }

  private toIconType(value: unknown): IconType | undefined {
    return value === 'emoji' || value === 'text' || value === 'mat' ? value : undefined;
  }

  private resolvePalette(status: string): { color: string; backgroundColor: string; textColor?: string } {
    const normalizedStatus = this.sanitizeCssClass(status);
    const palettes: Record<string, { color: string; backgroundColor: string; textColor?: string }> = {
      green: { color: 'var(--pat-green, #16a34a)', backgroundColor: 'var(--pat-green-bg, #dcfce7)', textColor: 'var(--pat-green, #16a34a)' },
      yellow: { color: 'var(--pat-yellow, #d97706)', backgroundColor: 'var(--pat-yellow-bg, #fff7ed)', textColor: 'var(--pat-yellow, #d97706)' },
      amber: { color: 'var(--pat-yellow, #d97706)', backgroundColor: 'var(--pat-yellow-bg, #fff7ed)', textColor: 'var(--pat-yellow, #d97706)' },
      red: { color: 'var(--pat-red, #ef4444)', backgroundColor: 'var(--pat-red-bg, #fee2e2)', textColor: 'var(--pat-red, #ef4444)' },
      danger: { color: 'var(--pat-red, #ef4444)', backgroundColor: 'var(--pat-red-bg, #fee2e2)', textColor: 'var(--pat-red, #ef4444)' },
      blue: { color: 'var(--pat-blue, #2563eb)', backgroundColor: 'var(--pat-blue-bg, #dbeafe)', textColor: 'var(--pat-blue, #2563eb)' },
      neutral: { color: 'var(--pat-neutral, #111827)', backgroundColor: 'var(--pat-neutral-bg, #e5e7eb)' },
      ...(this.isRecord(this.config['palettes']) ? this.config['palettes'] : {}),
    };

    return palettes[normalizedStatus] || palettes['neutral'];
  }

  private sanitizeCssClass(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9_-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  }

  private isRecord(value: unknown): value is Record<string, any> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }
}
